import { APP_PREFIX_PATH } from 'configs/AppConfig';

// NOTE: copy is inline EN while we iterate on the flow visually.
// Once the wording is locked we extract every title/body/cta into i18n (5 locales).

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Find the nearest actually-scrollable ancestor (mobile product page scrolls an
// inner container, not window — so window.scrollTo / scrollIntoView do nothing).
const getScrollParent = (el) => {
  let node = el?.parentElement;
  while (node) {
    const s = getComputedStyle(node);
    if (/(auto|scroll)/.test(s.overflowY + s.overflow) && node.scrollHeight > node.clientHeight + 4) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

// First VISIBLE match — desktop/mobile share selectors (e.g. `.catalog-wishlist-link`
// exists in both the desktop header and the mobile header). document.querySelector
// returns the FIRST in DOM order, which may be the display:none one — scrolling to a
// hidden element is a no-op. Pick the one that's actually rendered.
const qVisible = (selector) => {
  const all = document.querySelectorAll(selector);
  for (const el of all) {
    if (el.getClientRects().length) return el;
  }
  return null;
};

// Scroll an element to near the top of whatever container actually scrolls it,
// leaving `offset` px of room above it. Works for both window and inner containers.
const scrollElToTop = (selector, offset = 72) => {
  const el = qVisible(selector);
  if (!el) return;
  const sp = getScrollParent(el);
  if (sp) {
    sp.scrollTop += el.getBoundingClientRect().top - sp.getBoundingClientRect().top - offset;
  } else {
    window.scrollTo({ top: Math.max(0, window.scrollY + el.getBoundingClientRect().top - offset), behavior: 'auto' });
  }
};

// Real template cards only. The "Custom design" add-card shares the
// `.catalog-tpl-image` class, and clicking it opens the custom-design modal — so
// every auto-select must exclude it (`:not(.catalog-tpl-add-card)`), otherwise on
// a brand-new account (0 real templates yet, still generating) the tour would pop
// that modal mid-flow.
const REAL_TPL_IMG = '.catalog-tpl-card:not(.catalog-tpl-add-card) .catalog-tpl-image';

// The scripted demo product. It MUST exist in every company's catalog + brand
// store, otherwise the search/brand-store steps land on an empty list. "Hoodie -
// The Rex" was only seeded in some companies; "T-shirt - The Sterling" is present
// in all. Single source of truth — change it here to swap the demo product everywhere.
const DEMO_PRODUCT = 'T-shirt - The Sterling';
const DEMO_PRODUCT_RE = /sterling/i;

// Ensure a real template is selected (unlocks pricing / enables the add buttons).
// The gallery step deselects it to show the base product, so steps that come
// after — pricing, add-to-quote, add-to-brand-store — must re-ensure it when the
// user navigates Back into them.
//
// FIRE-AND-FORGET: this must NEVER block a step from appearing. The template
// request is guaranteed to return, so the tour lands on the section immediately
// and the selection lands whenever the template arrives (polled up to `timeout`).
const ensureTemplateSelected = (timeout = 30000) => {
  if (document.querySelector('.catalog-tpl-image-active')) return;
  const start = Date.now();
  (async () => {
    while (Date.now() - start < timeout) {
      if (document.querySelector('.catalog-tpl-image-active')) return; // selected meanwhile
      const tplImage = document.querySelector(REAL_TPL_IMG);
      if (tplImage) {
        tplImage.closest('.catalog-tpl-card')?.click();
        return;
      }
      await wait(300);
    }
  })();
};

// Scroll `selector` into view as soon as it exists (polled up to `timeout`),
// WITHOUT blocking. For targets that render only after an async step — e.g. the
// pricing grid appears only once a template is selected.
const scrollIntoViewSoon = (selector, opts, timeout = 30000) => {
  const start = Date.now();
  (async () => {
    while (Date.now() - start < timeout) {
      const el = qVisible(selector);
      if (el) {
        el.scrollIntoView(opts);
        return;
      }
      await wait(300);
    }
  })();
};

// Select a priced cell in the RIGHT-most timeline column (near `rowFraction` down
// the grid, ~500 pcs) so the highlighted (purple) cell sits on a real price, then
// scroll that cell into view. Does NOT depend on a pre-existing selection — it
// creates one by clicking. Fire-and-forget; polls until the right column has
// priced (non-disabled) cells.
const selectRightPriceCellSoon = (rowFraction = 0, timeout = 30000) => {
  const start = Date.now();
  (async () => {
    while (Date.now() - start < timeout) {
      const cols = document.querySelectorAll('.pg-timeline-col');
      const rightCol = cols[cols.length - 1];
      if (rightCol) {
        const cards = Array.from(rightCol.querySelectorAll('.pg-card'));
        const enabled = cards.filter((c) => !c.classList.contains('pg-card-disabled'));
        if (enabled.length) {
          const already = rightCol.querySelector('.pg-card-active');
          if (!already) {
            // Enabled cell nearest the target row (0 = top / 100 pcs).
            const wanted = Math.round(rowFraction * (cards.length - 1));
            const cell = enabled.reduce(
              (best, c) =>
                Math.abs(cards.indexOf(c) - wanted) < Math.abs(cards.indexOf(best) - wanted) ? c : best,
              enabled[0],
            );
            cell.click();
          }
          // Align the grid's top into view (100 pcs sits at the top — no deep scroll
          // to a mid cell). Leave room for the fixed page header.
          scrollElToTop('.pg', 90);
          return;
        }
      }
      await wait(300);
    }
    scrollElToTop('.pg', 90); // fallback: at least bring the grid into view
  })();
};

const catalogHome = `${APP_PREFIX_PATH}/catalog`;

// Route matchers — decide whether a step is allowed to show on the current path.
const isCatalogHome = (p) => p === catalogHome || p === `${catalogHome}/`;
const isProductDetail = (p) =>
  new RegExp(`^${APP_PREFIX_PATH}/catalog/products/[^/]+/?$`).test(p);
const isBrandStore = (p) => p.startsWith(`${APP_PREFIX_PATH}/brand-store`);
// Sidebar steps can run on either catalog or brand store (the app sidebar is on both).
const hasSidebar = (p) => isCatalogHome(p) || isProductDetail(p) || isBrandStore(p);

export const buildSteps = ({ isMobile } = {}) => [
  {
    id: 'welcome',
    match: isCatalogHome,
    selector: null,
    placement: 'center',
    title: 'Welcome to Sunday platform',
    body: 'This is where you find every product available for your brand. Let us take a quick tour so you know your way around.',
    nextLabel: "Let's go",
  },
  {
    id: 'brandToggle',
    match: isCatalogHome,
    selector: '.brand-toggle-hover-border',
    placement: 'bottom',
    pointerAt: { x: 0.9, y: 0.85 }, // point at the switch on the right, not the label
    title: 'See it in your brand',
    body: 'When this is on, every product is shown in your brand colors and logo. Turn it off to see the standard product photos instead.',
  },
  {
    id: 'search',
    match: isCatalogHome,
    // Desktop uses the inline search trigger; mobile uses the top-bar search icon.
    // queryVisible picks whichever is actually rendered at the current width.
    selector: '.catalog-search-trigger, .catalog-mobile-search-btn',
    placement: 'bottom',
    pointerAt: isMobile ? { x: 0.9, y: 0.5 } : { x: 0.15, y: 0.9 },
    title: 'Looking for something specific?',
    body: `Search any product by name. Hit Next and watch us pull up ${DEMO_PRODUCT} for you.`,
    // If we arrive here (e.g. Back from the results step), make sure the search modal is closed.
    onEnter: async () => {
      window.dispatchEvent(new CustomEvent('tour-catalog-search-close'));
      await wait(150);
    },
  },
  {
    id: 'searchResults',
    match: isCatalogHome,
    selector: '.catalog-search-result-card',
    placement: 'bottom',
    // Modal-in-modal: keep the tour dim OFF while typing (so the search modal + typing
    // stay visible), then turn on the dark spotlight once results are in, highlighting the product.
    backdropWhenReady: true,
    title: 'Here are your results',
    body: `We searched for "${DEMO_PRODUCT}". Let us open this one so you can see a product up close.`,
    onEnter: async () => {
      window.dispatchEvent(
        new CustomEvent('tour-catalog-search-fill', { detail: { text: DEMO_PRODUCT } })
      );
      await wait(1900); // typing animation + debounced results
    },
    onNext: async ({ navigate }) => {
      const cards = Array.from(document.querySelectorAll('.catalog-search-result-card'));
      const target = cards.find((c) => DEMO_PRODUCT_RE.test(c.textContent || '')) || cards[0];
      if (target) {
        target.click();
      } else {
        navigate(`${APP_PREFIX_PATH}/catalog/products?search=${encodeURIComponent(DEMO_PRODUCT)}`);
      }
      await wait(700);
    },
  },
  {
    id: 'gallery',
    match: isProductDetail,
    // Desktop: spotlight the whole left column (thumbs + preview + CSM). Mobile: the
    // column is full-width, so spotlight just the gallery block and place the card below.
    selector: isMobile ? '.catalog-product-gallery' : '.catalog-col-left',
    placement: isMobile ? 'bottom' : 'right',
    // Desktop: point the hand right at the 2nd thumbnail (the video). Mobile: a
    // fraction of the (full-width) gallery block.
    pointerSelector: isMobile ? undefined : '.catalog-gallery-thumbs .catalog-gallery-thumb:nth-of-type(2)',
    pointerAt: isMobile ? { x: 0.1, y: 0.25 } : { x: 0.5, y: 0.5 },
    title: 'Every angle',
    body: 'These are the different views of the product. Click through to see it from every side.',
    onEnter: async () => {
      if (!isMobile) {
        // Desktop: reset scroll to the very top so the product shows naturally
        // (header + gallery), instead of scrolling down onto the CSM card.
        window.scrollTo({ top: 0 });
        let node = document.querySelector('.catalog-col-left')?.parentElement;
        while (node) {
          if (node.scrollTop > 0) node.scrollTop = 0;
          node = node.parentElement;
        }
        await wait(400);
      }
      // If we came Back from the template step, a template is still selected and the
      // preview shows it — deselect it so the base product is shown again.
      const activeTpl = document.querySelector('.catalog-tpl-image-active');
      if (activeTpl) {
        const card = activeTpl.closest('.catalog-tpl-card');
        if (card) card.click();
        await wait(300);
      }
      // Prefer showing the product video if one exists (more engaging); the video
      // thumb is the .catalog-gallery-thumb that contains .catalog-gallery-thumb-video.
      const videoThumb = document.querySelector('.catalog-gallery-thumb-video');
      if (videoThumb) {
        videoThumb.closest('.catalog-gallery-thumb')?.click();
      } else {
        const thumbs = document.querySelectorAll('.catalog-gallery-thumb');
        if (thumbs[0]) thumbs[0].click();
      }
      await wait(300);
      if (isMobile) {
        // Mobile: scroll the gallery to the top LAST — after the template deselect +
        // thumb click, which change the gallery height and would otherwise push it back
        // below the fold. The mobile product page scrolls an inner container (not
        // window), so use the scroll-parent helper.
        scrollElToTop('.catalog-product-gallery', 72);
        await wait(350);
      }
    },
  },
  {
    id: 'templates',
    match: isProductDetail,
    selector: '.catalog-template-section',
    // Desktop: card to the left of the section (preview is on the left column).
    // Mobile: single column, so put the card below and point at the first card.
    placement: isMobile ? 'bottom' : 'left',
    pointerAt: isMobile ? { x: 0.15, y: 0.45 } : { x: 0.1, y: 0.78 },
    title: 'Pick a template',
    body: isMobile
      ? 'Ready-made designs used by world-class brands. Choose one and watch the product preview update instantly.'
      : 'Ready-made designs used by world-class brands. Choose one and watch the preview on the left update instantly.',
    // While a brand-new account is still generating its designs, the carousel
    // shows a "Generating templates" spinner (nothing to pick yet). Swap the copy
    // so the card explains the wait instead of telling the user to "choose one".
    // The renderer polls loadingSelector and auto-reverts to the normal copy the
    // moment the first real template lands.
    loadingSelector: '.catalog-tpl-card-skeleton',
    // This is the one step where the user may actually POKE the page: let clicks
    // reach the real template cards + the two carousel arrows (so they can browse
    // and pick a design and watch the preview change). Everything else stays
    // blocked — notably the "Custom design" card, which is to the right of the
    // carousel and would open the upload modal. The shield opens a hole over the
    // union of these elements' rects.
    interactiveSelectors: ['.catalog-tpl-arrow', '.catalog-tpl-carousel-wrap'],
    titleLoading: 'Generating your designs',
    bodyLoading:
      "First time here, so we're generating your brand designs right now, they'll appear in a moment. These are ready-made templates used by world-class brands, pick one as soon as they show up.",
    onEnter: async () => {
      // Land on the template section IMMEDIATELY — don't wait for the (guaranteed)
      // template request. On mobile align to the top so the section sits above the
      // bottom-placed card.
      const section = document.querySelector('.catalog-template-section');
      if (section) section.scrollIntoView({ behavior: 'smooth', block: isMobile ? 'start' : 'center' });
      // Select the first real template whenever it arrives — in the background, so
      // the step is never held on the spinner. (Excludes the "Custom design" card,
      // whose click opens the upload modal.)
      ensureTemplateSelected();
    },
  },
  {
    id: 'customDesign',
    match: isProductDetail,
    // Spotlight ONLY the "Custom design / Request free design" card (to the right of
    // the template carousel). Not interactive — the shield keeps the card blocked so
    // the upload modal can't open mid-tour; this step just presents it.
    selector: '.catalog-tpl-add-card',
    placement: isMobile ? 'bottom' : 'left',
    pointerSelector: '.catalog-tpl-add-cta-btn', // hand on the "Request free design" button
    pointerAt: { x: 0.5, y: 0.85 },
    title: 'Have your own design?',
    body: 'If you already have your own design, hit Request free design and our team will make it for you, custom and free.',
    onEnter: async () => {
      // Bring the custom-design card into view. On mobile, leave room for the sticky
      // header (scrollElToTop with an offset) so the top of the card (Free chip /
      // icon) isn't cut off behind it.
      if (isMobile) {
        scrollElToTop('.catalog-tpl-add-card', 90);
      } else {
        scrollIntoViewSoon('.catalog-tpl-add-card', { behavior: 'smooth', block: 'center' });
      }
    },
  },
  {
    id: 'pricing',
    match: isProductDetail,
    selector: '.pg',
    // Desktop: card to the left of the grid. Mobile: full width, so put the card
    // below and scroll the active price cell to the top (stays visible above it).
    placement: isMobile ? 'bottom' : 'left',
    // Point at the RIGHT-most timeline column (the faster one that actually shows
    // prices) on the TOP row (100 pcs) — not the left column, which is often empty.
    pointerSelector: '.pg-timeline-col:last-child .pg-col-body',
    pointerAt: { x: 0.5, y: 0.06 },
    title: 'Quantity and pricing',
    body: 'Set your quantity and see the price per piece. The more you order, the lower the price. You can also compare production times.',
    onEnter: async () => {
      // Select a template in the background (unlocks the grid) and scroll the grid
      // into view as soon as it renders — never block the step waiting for it.
      ensureTemplateSelected();
      // Select the ~500-pcs cell in the right (priced) column and scroll it into
      // view — non-blocking; the helper falls back to the whole grid if the priced
      // cells aren't there yet.
      selectRightPriceCellSoon();
    },
  },
  {
    id: 'addQuote',
    match: isProductDetail,
    // Desktop button is hidden < 768; mobile has a sticky bottom bar button instead.
    selector: '.catalog-cart-btn, [data-tour-id="add-to-quote-mobile"]',
    placement: 'top',
    title: 'Interested in this product?',
    body: 'Add it to your quote with the Add to Quote button and someone from our team will reach out to help you.',
    onEnter: async () => {
      ensureTemplateSelected(); // background — never block the step
      const el =
        document.querySelector('.catalog-cart-btn') ||
        document.querySelector('[data-tour-id="add-to-quote-mobile"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  },
  // Wishlist step. The "Listed in N wishlists" link lives in the desktop product
  // header AND (now) the mobile header — same `.catalog-wishlist-link` class — so the
  // step runs on both (that's why desktop=16 / mobile=16).
  {
    id: 'wishlist',
    match: isProductDetail,
    selector: '.catalog-wishlist-link',
    placement: 'bottom',
    pointerAt: { x: 0.5, y: 0.85 },
    title: 'Share it with colleagues',
    body: 'Add it to a wishlist to save the products you like and share them with your colleagues.',
    onEnter: async () => {
      // Mobile: the wishlist button is in the sticky mobile header at the top — bring
      // it below the header. Desktop: it's in the product header, center it.
      if (isMobile) {
        scrollElToTop('.catalog-wishlist-link', 90);
      } else {
        scrollIntoViewSoon('.catalog-wishlist-link', { behavior: 'smooth', block: 'center' });
      }
    },
  },
  {
    id: 'addBrandStore',
    match: isProductDetail,
    // Desktop button is hidden < 1200; mobile renders a separate button.
    selector: '[data-tour-id="add-to-brand-store"], [data-tour-id="add-to-brand-store-mobile"]',
    placement: 'bottom',
    title: 'Save it to your Brand store',
    body: 'Really sure about this product? Add it to your Brand store, where your approved company merch lives so your whole team can order it directly. It is a Sunday invention that makes managing company merch effortless.',
    onEnter: async () => {
      ensureTemplateSelected(); // background — never block the step
      if (isMobile) {
        // The mobile add-to-brand-store button lives in the (scroll-away) mobile
        // header at the top; the desktop one is display:none. Scroll the VISIBLE
        // mobile button near the top so the spotlight isn't off-screen.
        scrollElToTop('[data-tour-id="add-to-brand-store-mobile"]', 90);
      } else {
        const el = document.querySelector('[data-tour-id="add-to-brand-store"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
  },
  {
    id: 'brandStoreProduct',
    match: isBrandStore,
    // Land on the Brand store HOME and scroll down to the products section — reads as
    // "this is where your products live" (more logical than dropping the user onto a
    // filtered list). The grid always renders (even before the async seed lands), so
    // no readiness gate is needed and the copy works whether it's populated yet or not.
    goTo: `${APP_PREFIX_PATH}/brand-store`,
    selector: '.brand-store-products-grid',
    placement: 'top',
    pointerSelector: '.brand-store-products-grid',
    pointerAt: { x: 0.15, y: 0.15 }, // hand on the first product tile
    title: 'It is in your Brand store',
    body: 'This is your Brand store. From now on, the products you add show up right here, ready for your whole team to order directly.',
    onEnter: async ({ navigate }) => {
      // Make sure we're on the Brand store HOME (Back can leave us on a sub-page).
      if (!/\/brand-store\/?$/.test(window.location.pathname)) {
        navigate(`${APP_PREFIX_PATH}/brand-store`);
      }
      // Scroll to the "All products" section — but only AFTER its products have
      // loaded. The home page loads collections + products async; scrolling too early
      // lands on the "Your collections" section above, because the layout still
      // shifts down as data arrives. Wait for a product tile, then align to the top.
      const start = Date.now();
      while (Date.now() - start < 60000) {
        if (document.querySelector('.brand-store-products-grid a[href*="/brand-store/products/"]')) break;
        await wait(300);
      }
      scrollElToTop('#products-section', 80);
    },
  },
  {
    id: 'orders',
    match: hasSidebar,
    title: 'Track your orders',
    body: 'Every order you place shows up here, with all the details in one place.',
    // Desktop: open the sidebar + spotlight the item. Mobile: open the hamburger
    // menu (mobileMenu flag) + spotlight the menu item.
    ...(isMobile
      ? { mobileMenu: true, noBackdrop: true, selector: '[data-tour-id="mobile-menu-orders"]', placement: 'bottom' }
      : { expandSidebar: true, selector: '[data-tour-id="brand-store-orders"], [data-tour-id="nav-orders"]', placement: 'right', pointerAt: { x: 0.85, y: 0.8 } }),
  },
  {
    id: 'agency',
    match: hasSidebar,
    title: 'Managing multiple brands?',
    body: 'Agency lets you manage several brands or divisions in one place, so everything stays organized under one roof.',
    ...(isMobile
      ? { mobileMenu: true, noBackdrop: true, selector: '[data-tour-id="mobile-menu-agency"]', placement: 'bottom' }
      : { expandSidebar: true, selector: '[data-tour-id="agency"]', placement: 'right', pointerAt: { x: 0.85, y: 0.8 } }),
  },
  {
    id: 'wardrobe',
    match: hasSidebar,
    title: 'Let us handle logistics',
    body: 'We can store, manage and ship your merch for you, and help you measure the results.',
    ...(isMobile
      ? { mobileMenu: true, noBackdrop: true, selector: '[data-tour-id="mobile-menu-wardrobe"]', placement: 'bottom' }
      : { expandSidebar: true, selector: '[data-tour-id="wardrobe-menu"]', placement: 'right', pointerAt: { x: 0.85, y: 0.8 } }),
  },
  {
    id: 'invite',
    match: hasSidebar,
    title: 'Invite your team',
    body: 'The platform is stronger when you collaborate. Inviting colleagues is completely free, there are no costs involved. Invite whoever needs access, and have fun!',
    // Final step: the primary button opens the Invite modal (clicks the Invite
    // avatars), then the tour finishes — leaving the modal open for the user.
    nextLabel: 'Invite',
    onNext: async () => {
      const btn =
        document.querySelector('.actions-section .share-avatars') ||
        document.querySelector('.mobile-menu .share-avatars') ||
        document.querySelector('.share-avatars');
      if (btn) {
        btn.click();
        await wait(300);
      }
    },
    ...(isMobile
      ? { mobileMenu: true, noBackdrop: true, selector: '.mobile-menu .share-avatars', placement: 'top' }
      : {
          // Desktop: highlight the Invite button in the top bar; scroll up so it shows.
          selector: '.actions-section .share-avatars',
          placement: 'bottom',
          pointerAt: { x: 0.5, y: 0.95 },
          onEnter: async () => {
            window.scrollTo({ top: 0 });
            let node = document.querySelector('.actions-section .share-avatars')?.parentElement;
            while (node) {
              if (node.scrollTop > 0) node.scrollTop = 0;
              node = node.parentElement;
            }
            await wait(450);
          },
        }),
  },
];
